/**
 * ChatContextualChips — stage-based CTAs rendered below a pure-text
 * assistant response (i.e. no tools fired, as happens after a capability /
 * onboarding reply).
 *
 * Stage is derived from two signals available without any extra API call:
 *   1. chatStore.dealContext — is a deal in context for this session?
 *   2. termSheetCount prop  — from the most recent get_deal result, if any.
 *
 * Stages:
 *   NO_DEAL     — no deal in context → guide to create one
 *   DEAL_NO_TS  — deal in context, TS count 0 or unknown → guide to upload
 *   DEAL_ONE    — deal in context, 1 analysed TS → score / covenant review
 *   DEAL_MULTI  — deal in context, 2+ TS → cross-offer comparison
 *
 * Max 3 chips per stage (matching orchestration spec §1).
 */
import { useChatStore } from '../../../stores/chatStore';
import { useChatTelemetry } from '../hooks/useChatTelemetry';

interface Props {
  onPick: (prompt: string) => void;
  /** From the most recent get_deal tool result in the conversation history.
   * undefined when no get_deal has been called this session. */
  termSheetCount?: number;
}

interface ContextChip {
  id: string;
  label: string;
  prompt: string;
}

type Stage = 'no_deal' | 'deal_no_ts' | 'deal_one' | 'deal_multi';

const STAGE_CHIPS: Record<Stage, ContextChip[]> = {
  no_deal: [
    {
      id: 'ctx-create-deal',
      label: 'Create a deal',
      prompt: 'I want to create a deal and upload a term sheet for analysis.',
    },
    {
      id: 'ctx-how-works',
      label: 'How does Credia work?',
      prompt: 'Walk me through how Credia works and what you can analyse for me.',
    },
  ],
  deal_no_ts: [
    {
      id: 'ctx-upload-ts',
      label: 'Upload a term sheet',
      prompt: 'I want to upload a term sheet for this deal.',
    },
    {
      id: 'ctx-deal-status',
      label: "What's the status of this deal?",
      prompt: 'What is the current status of this deal?',
    },
  ],
  deal_one: [
    {
      id: 'ctx-walk-score',
      label: 'Walk me through my score',
      prompt: 'Walk me through the scoring for this term sheet — what does each component mean?',
    },
    {
      id: 'ctx-explain-covenants',
      label: 'Explain my covenants',
      prompt: 'Explain the covenants in this term sheet. Are any of them unusual?',
    },
    {
      id: 'ctx-compare-market',
      label: 'How does it compare to the market?',
      prompt: 'How does this term sheet compare to what other Belgian companies are getting?',
    },
  ],
  deal_multi: [
    {
      id: 'ctx-compare-offers',
      label: 'Compare my offers',
      prompt: 'Compare the term sheets for this deal. Which one is better and why?',
    },
    {
      id: 'ctx-whats-driving-score',
      label: "What's driving my score?",
      prompt: 'What is driving the score for this deal and where do I have the most room to improve?',
    },
  ],
};

function deriveStage(
  hasDealContext: boolean,
  termSheetCount: number | undefined,
): Stage {
  if (!hasDealContext) {return 'no_deal';}
  if (typeof termSheetCount !== 'number' || termSheetCount === 0) {return 'deal_no_ts';}
  if (termSheetCount === 1) {return 'deal_one';}
  return 'deal_multi';
}

export function ChatContextualChips({ onPick, termSheetCount }: Props) {
  const { logEvent } = useChatTelemetry();
  const dealContext = useChatStore((s) => s.dealContext);

  const stage = deriveStage(Boolean(dealContext), termSheetCount);
  const chips = STAGE_CHIPS[stage].slice(0, 3);

  if (chips.length === 0) {return null;}

  return (
    <div className="mt-2 flex flex-wrap gap-2">
      {chips.map((chip, position) => (
        <button
          key={chip.id}
          type="button"
          onClick={() => {
            logEvent('contextual_chip_clicked', {
              chip_id: chip.id,
              stage,
              position,
              has_deal: Boolean(dealContext),
              term_sheet_count: termSheetCount,
            });
            onPick(chip.prompt);
          }}
          className="border border-border bg-surface-card px-3 py-1 text-body-sm text-txt-secondary transition-colors hover:border-orange hover:text-orange"
        >
          {chip.label}
        </button>
      ))}
    </div>
  );
}
